import { ContactModel } from '../components/contact.model';
import { UserSummarySettingsModel } from './user-summary-settings.model';
import { ILightWeightObject } from '@fi-shared/models';
import { FiAddressTypes } from '../core/address-types';
import { getDefaultCountryId } from '../core/country-id';
import { AddressModel } from '../utilities/address/address.model';
import { FiApplicationLanguageType } from '../shared/fi-content-language-shared/fi-application-language-type';
import { IFiCustomerData } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { IUserModel } from '@fi-shared/models/user.model';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { IFiUserModuleAccessRights } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiContactInformationTypes } from '@fi-shared/types/fi-contact-information-types';
import { IContactModel, IDataFieldValueModel } from '@fi-shared/models/common';
import { AvailableDateTimeLocale } from '@fi-shared/utils';

export class UserModel {
    Id = 0;
    FirstName = '';
    LastName = '';
    FullName = '';
    MediaBankPhysicalFileName = '';
    MediaBankExtension = '';
    LoginName: string = '';
    LoginContactId: number;
    Password: string = '';
    ConfirmPassword: string = '';
    IsActivated: boolean = true;
    IsExpired: boolean = false;
    ApplicationLanguageType: FiApplicationLanguageType;
    ApplicationLanguageTypeOrig: FiApplicationLanguageType;
    PreferredContentLanguageId: number;
    PreferredBusinessUnitId: number = 1;
    PreferredBusinessUnitCountryId: number;
    PreferredBusinessUnitName: string = '';
    PreferredDateFormat: AvailableDateTimeLocale;
    PreferredTimeFormat: AvailableDateTimeLocale;
    CreatedByUserId: number = 0;
    CreatedByUserName: string = '';
    CreatedOn: string; //ISO 8601 date
    ChangedByUserId: number = 0;
    ChangedByUserName: string = '';
    ChangedOn: string; //ISO 8601 date
    Addresses: AddressModel[] = [];
    BusinessUnits: ILightWeightObject[] = [];
    UserToBusinessUnitIds: number[] = [];
    BusinessAreas: ILightWeightObject[] = [];
    Areas: ILightWeightObject[] = [];
    UserToAreaIds: number[] = [];
    Roles: ILightWeightObject[] = [];
    UserToRoleIds: number[] = [];
    UserToBusinessAreaIds: number[] = []; // we use this only on frontend
    EmailAddressContacts: IContactModel[] = [];
    PhoneNumberContacts: IContactModel[] = [];
    IsSystemObject: boolean = false;
    StartDate: string;
    EndDate: string;
    IsUserActive: boolean = false;
    IsLocked: boolean = false;
    MediaBankFileReference: IMediaBankFileReference = null;
    OriginalMediaBankFileReference: IMediaBankFileReference = null;
    IsAccessRequesting: boolean = false;
    Description: string = '';
    WorkEmailAddressContact: string = '';
    Tags: ILightWeightObject[] = [];
    TagIds: number[] = [];
    LatestActivity: string;
    RecaptchaResponse: string;
    DataFieldValues: IDataFieldValueModel[] = [];
    HasEditAccess = false;
    //IsPKUser: boolean;

    SummarySettings = new UserSummarySettingsModel();

    ModuleAccessRights: IFiUserModuleAccessRights;
    SendMagicLink: boolean;

    constructor(customerData: IFiCustomerData) {
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        this.StartDate = startDate as any;
        this.ApplicationLanguageType = customerData.DefaultApplicationLanguageType;
        this.ApplicationLanguageTypeOrig = customerData.DefaultApplicationLanguageType;
    }

    Initialize(user: IUserModel) {
        this.Id = user.Id;
        this.FirstName = user.FirstName;
        this.LastName = user.LastName;
        this.FullName = user.FullName;
        this.LoginName = user.LoginName;
        this.LoginContactId = user.LoginContactId;
        this.ApplicationLanguageTypeOrig = this.ApplicationLanguageType = user.ApplicationLanguageType;
        this.PreferredContentLanguageId = user.PreferredContentLanguageId;
        this.PreferredBusinessUnitId = user.PreferredBusinessUnitId;
        this.PreferredBusinessUnitCountryId = user.PreferredBusinessUnitCountryId;
        this.PreferredBusinessUnitName = user.PreferredBusinessUnitName;
        this.PreferredDateFormat = user.PreferredDateFormat;
        this.PreferredTimeFormat = user.PreferredTimeFormat;
        this.CreatedByUserId = user.CreatedByUserId;
        this.CreatedByUserName = user.CreatedByUserName;
        this.CreatedOn = user.CreatedOn;
        this.ChangedByUserId = user.ChangedByUserId;
        this.ChangedByUserName = user.ChangedByUserName;
        this.ChangedOn = user.ChangedOn;
        this.StartDate = user.StartDate;
        this.EndDate = user.EndDate;
        this.IsActivated = user.IsActivated;
        this.IsExpired = user.IsExpired;
        this.Roles = user.Roles;
        this.UserToRoleIds = user.UserToRoleIds;
        this.Areas = user.Areas;
        this.UserToAreaIds = user.UserToAreaIds;
        this.BusinessAreas = user.BusinessAreas;
        this.BusinessUnits = user.BusinessUnits;
        this.UserToBusinessUnitIds = user.UserToBusinessUnitIds;
        this.MediaBankPhysicalFileName = user.MediaBankPhysicalFileName;
        this.MediaBankExtension = user.MediaBankExtension;
        this.IsSystemObject = user.IsSystemObject || false;
        this.IsUserActive = user.IsUserActive || false;
        this.IsLocked = user.IsLocked || false;
        this.MediaBankFileReference = user.MediaBankFileReference;
        this.OriginalMediaBankFileReference = user.OriginalMediaBankFileReference || user.MediaBankFileReference;
        this.IsAccessRequesting = user.IsAccessRequesting;
        this.Description = user.Description;
        //this.IsPKUser = user.IsPKUser;

        const workEmailAddressContacts: IContactModel[] = user.EmailAddressContacts
            ? user.EmailAddressContacts.filter((emailAddressContact: ContactModel) => {
                  return (
                      emailAddressContact.ContactType === FiContactInformationTypes.WorkEmailAddress &&
                      emailAddressContact.IsDefault
                  );
              })
            : [];
        if (workEmailAddressContacts.length) {
            this.WorkEmailAddressContact = workEmailAddressContacts[0].ContactValue;
        }

        this.Tags = user.Tags;
        this.TagIds = user.TagIds;

        if (user.LatestActivity) {
            this.LatestActivity = new Date(user.LatestActivity) as any;
        }

        if (user.SummarySettings) {
            this.SummarySettings.initialize(user.SummarySettings);
        }
        this.ModuleAccessRights = user.ModuleAccessRights;
        this.DataFieldValues = user.DataFieldValues;
        this.HasEditAccess = user.HasEditAccess;
    }

    InitializeContacts(response: IUserModel, user: UserModel) {
        if (response && response.EmailAddressContacts && !!response.EmailAddressContacts.length) {
            response.EmailAddressContacts.forEach((emailAddressContact) => {
                user.EmailAddressContacts.push(emailAddressContact);
            });
        }

        if (response && response.PhoneNumberContacts && !!response.PhoneNumberContacts.length) {
            response.PhoneNumberContacts.forEach((phoneNumberContact) => {
                user.PhoneNumberContacts.push(phoneNumberContact);
            });
        }
    }

    InitializeAddresses(response, user) {
        if (response && response.Addresses && !!response.Addresses.length) {
            response.Addresses.forEach((address) => {
                user.Addresses.push(new AddressModel(address));
            });
        }
    }

    isLocked(): boolean {
        return this.IsLocked || this.IsExpired || !this.IsActivated;
    }

    anyTags(): boolean {
        return !!this.Tags.length;
    }

    anyEmails(): boolean {
        return !!this.EmailAddressContacts.length && !!this.EmailAddressContacts[0].ContactValue;
    }

    anyPhones(): boolean {
        return !!this.PhoneNumberContacts.length && !!this.PhoneNumberContacts[0].ContactValue;
    }

    addDefaultContacts() {
        const emailAddressContactToAdd = new ContactModel();
        emailAddressContactToAdd.ContactType = FiContactInformationTypes.WorkEmailAddress;
        emailAddressContactToAdd.IsDefault = true;
        this.EmailAddressContacts.push(emailAddressContactToAdd);
    }

    addDefaultAddresses() {
        const addressToAdd = new AddressModel();
        addressToAdd.AddressType = FiAddressTypes.HomeAddress;
        addressToAdd.IsDefault = true;
        this.Addresses.push(addressToAdd);
    }

    addDefaultPhoneNumbers(applicationLanguage: FiApplicationLanguageType) {
        const phoneNumberContactToAdd = new ContactModel();
        phoneNumberContactToAdd.ContactType = FiContactInformationTypes.CellPhoneNumber;
        phoneNumberContactToAdd.CountryId =
            this.PreferredBusinessUnitCountryId || getDefaultCountryId(applicationLanguage);
        phoneNumberContactToAdd.IsDefault = true;
        this.PhoneNumberContacts.push(phoneNumberContactToAdd);
    }

    checkAddDefaults(applicationLanguage: FiApplicationLanguageType) {
        if (!this.EmailAddressContacts.length) {
            this.addDefaultContacts();
        }

        if (!this.Addresses.length) {
            this.addDefaultAddresses();
        }

        if (!this.PhoneNumberContacts.length) {
            this.addDefaultPhoneNumbers(applicationLanguage);
        }
    }
}
